/**
 * UserShell, wraps user-mode routes with TopNav (no sidebar).
 *
 * Per D-087: default mode for all roles. Standard users (role='user') only
 * ever see this shell; admins/owners can flip to AdminShell via the gear
 * icon in TopNav.
 *
 * Auth gate: redirects to /sign-in if unauthenticated. Page-level role
 * checks (e.g., redirecting to /home from admin-only pages) stay in the
 * individual pages.
 *
 * D-168 wizard gate (Push #46): when a signed-in owner has
 * onboarding_completed_at IS NULL, redirect to /welcome BEFORE rendering
 * the requested user-mode route. The check is routing-only per D-160 —
 * we never gate data access on onboarding state, only navigation.
 *
 * Why here and not RootRedirect: post-sign-in landings come through both
 * `/` AND `/verify-email → navigate('/home')`. Gating inside UserShell
 * catches both paths in one place. We cache the fetched state in module
 * scope keyed by user_id so re-renders inside the same session don't
 * re-fetch, and so flipping the column in the wizard's complete handler
 * doesn't cause a loop back through here.
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import { TopNav } from './TopNav.tsx';
import { BrandedLoader } from './BrandedLoader.tsx';
import { EmailVerificationBanner } from './EmailVerificationBanner.tsx';
import { OnboardingIncompleteBanner } from './OnboardingIncompleteBanner.tsx';
import { LowBalanceBanner } from './LowBalanceBanner.tsx';
// Round 14 Item B (2026-06-25) — full FooterChrome no longer mounted on
// authenticated shells (Linear / Anthropic Console / Stripe Dashboard
// convention: no persistent in-app legal nav). Push 72 (2026-07-17) —
// the small "Powered by Olytic" attribution IS mounted here (corner
// variant) so the platform nod survives a workspace rebrand, matching
// the Salesforce Community pattern Ean requested.
import { PoweredByOlytic } from './PoweredByOlytic.tsx';
import { fetchOnboardingState } from '../lib/api.ts';
import { scrollStorageKey } from '../hooks/useScrollToTop.tsx';

// Module-scope cache so reentry into UserShell (route changes, hot reload
// of an inner page) doesn't re-fetch. Cleared if user_id changes.
const onboardingCache: {
  userId: string | null;
  completedAt: string | null;
  loaded: boolean;
} = { userId: null, completedAt: null, loaded: false };

/**
 * Called by /welcome's finish handler after POST /api/onboarding/complete
 * resolves, so the next mount of UserShell doesn't loop back to /welcome
 * before the server-side state propagates. Side-effect-free if cache is
 * already populated for someone else (defensive cross-user case).
 */
export function markOnboardingCompletedLocally(userId: string): void {
  if (onboardingCache.userId === userId) {
    onboardingCache.completedAt = new Date().toISOString();
    onboardingCache.loaded = true;
  } else {
    onboardingCache.userId = userId;
    onboardingCache.completedAt = new Date().toISOString();
    onboardingCache.loaded = true;
  }
}

export function UserShell() {
  const { status, user } = useSession();
  const location = useLocation();
  const [onboardingChecked, setOnboardingChecked] = useState(
    onboardingCache.loaded && onboardingCache.userId === (user?.user_id ?? null),
  );
  const [needsWelcome, setNeedsWelcome] = useState(
    onboardingCache.loaded &&
      onboardingCache.userId === (user?.user_id ?? null) &&
      onboardingCache.completedAt === null,
  );
  const fetchedFor = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated' || user === null) return;
    // Only the owner role hits the wizard. Skip the network call entirely
    // for admin / user roles.
    if (user.role !== 'owner') {
      onboardingCache.userId = user.user_id;
      onboardingCache.completedAt = new Date().toISOString();
      onboardingCache.loaded = true;
      setNeedsWelcome(false);
      setOnboardingChecked(true);
      return;
    }
    // Use cache when valid for this user.
    if (onboardingCache.loaded && onboardingCache.userId === user.user_id) {
      setNeedsWelcome(onboardingCache.completedAt === null);
      setOnboardingChecked(true);
      return;
    }
    if (fetchedFor.current === user.user_id) return;
    fetchedFor.current = user.user_id;
    fetchOnboardingState()
      .then((state) => {
        onboardingCache.userId = user.user_id;
        onboardingCache.completedAt = state.onboarding_completed_at;
        onboardingCache.loaded = true;
        setNeedsWelcome(state.onboarding_completed_at === null);
        setOnboardingChecked(true);
      })
      .catch(() => {
        // On error, don't block — assume completed. Failing the wizard
        // gate fails open so a broken endpoint doesn't trap the user.
        onboardingCache.userId = user.user_id;
        onboardingCache.completedAt = new Date().toISOString();
        onboardingCache.loaded = true;
        setNeedsWelcome(false);
        setOnboardingChecked(true);
      });
  }, [status, user]);

  if (status === 'loading') {
    return <BrandedLoader size="lg" message="Loading workspace…" />;
  }
  if (status === 'unauthenticated' || status === 'error' || user === null) {
    // Sub-agent HHHHH (2026-07-29) — protected-page session probe.
    // Any failure on /api/me (401, timeout, network) flips SessionProvider
    // to 'unauthenticated' (SESSION_FETCH_BUDGET_MS = 3s). We tack on
    // ?expired=1 so SignIn.tsx shows a friendly "Session expired, sign
    // in again" flash instead of the pristine sign-in landing — the
    // user knows what happened. The 'error' branch is kept as
    // defense-in-depth (SessionProvider no longer sets it, but a
    // future refactor might); redirect there behaves identically.
    //
    // Sub-agent IIIII (2026-07-29) — Track 5: preserve `?next=` so the
    // post-sign-in landing goes back to the page the user was actually
    // on when the session died. Skip when the current path is /home
    // (default landing anyway) or when we're already at /sign-in.
    // safeNextPath() in SignIn.tsx validates the value against the
    // allowlist so a hostile path from any source falls back to /home.
    const currentPath = location.pathname + location.search;
    const shouldPreserveNext =
      location.pathname !== '/home' &&
      location.pathname !== '/' &&
      !location.pathname.startsWith('/sign-in');
    // Sub-agent LLLLL (2026-07-29) — anti-punt for Push 102 Track 5 scroll
    // preservation. Stash the current window scroll offset alongside the
    // ?next= param so useScrollToTop can restore it on post-signin mount.
    // Skip when we don't preserve next (would land back at /home anyway)
    // or when there's nothing to preserve (scrollY = 0).
    if (shouldPreserveNext && typeof window !== 'undefined' && window.scrollY > 0) {
      try {
        sessionStorage.setItem(
          scrollStorageKey(currentPath),
          String(window.scrollY),
        );
      } catch {
        /* private browsing / disabled storage — no-op */
      }
    }
    const target = shouldPreserveNext
      ? `/sign-in?expired=1&next=${encodeURIComponent(currentPath)}`
      : '/sign-in?expired=1';
    return <Navigate to={target} replace />;
  }
  // Wait for the onboarding probe before deciding whether to render
  // the shell or punt to /welcome. Owner-only path; for admin/user the
  // effect short-circuits to onboardingChecked=true on the first tick.
  if (!onboardingChecked) {
    return <BrandedLoader size="lg" message="Loading workspace…" />;
  }
  if (needsWelcome && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <div className="app-shell user-mode">
      <TopNav mode="user" />
      {/* Phase 1.21 (2026-06-29): persistent verify-email banner. The
          banner self-hides when user.email_verified === true, so this
          mount only renders for unverified users. Sits between TopNav
          and the page content so it follows the user across every
          authenticated route (chat, market, profile, record details).
          Non-dismissible by design. verification is a hard ask. */}
      <EmailVerificationBanner />
      {/* Phase 1.32 (2026-06-29): safety-net banner for owners who landed
          here without finishing the wizard. Renders nothing for completed
          owners or non-owner roles. The routing gate above already
          redirects normal-path first-time owners to /welcome; this banner
          catches edge cases (manual URL nav, stale tabs, post-cache-clear). */}
      <OnboardingIncompleteBanner />
      {/* Push 4-1ah (2026-07-10): absolute low-balance banner (renders
          when balance drops below threshold). Complements the % consumption
          CreditThresholdBanner mounted per-page. Dismissible per-session
          via sessionStorage; self-hides at healthy balance. */}
      <LowBalanceBanner />
      <main className="app-main">
        <div className="app-content">
          <Outlet />
        </div>
      </main>
      {/* Push 72 — corner attribution. Renders when the workspace has
          customized branding so the platform nod stays visible after
          the rebrand. Suppressed for the vanilla Olytic-branded default
          because the TopNav wordmark already reads "Olytic" — a second
          attribution would be redundant. Hidden on narrow viewports via
          the .powered-by-olytic-corner CSS rule (mobile chat needs
          bottom real estate for the composer). */}
      {user?.brand_logo_r2_key !== null && user?.brand_logo_r2_key !== undefined && (
        <PoweredByOlytic variant="corner" />
      )}
    </div>
  );
}
